import { Button, Text } from '../../ui'
import { SettingsSection } from './SettingsSection'
import {
  useRecoverArticle,
  useRecoverCollection,
  useTrash,
} from '../../../hooks/useTrash'

type TrashSectionProps = {
  workspaceId: string
}

export function TrashSection({ workspaceId }: TrashSectionProps) {
  const { data: trashResult, isLoading } = useTrash(workspaceId)
  const recoverArticle = useRecoverArticle(workspaceId)
  const recoverCollection = useRecoverCollection(workspaceId)

  const trash = trashResult?.success ? trashResult.data : null
  const articles = trash?.articles ?? []
  const collections = trash?.collections ?? []
  const isEmpty = articles.length === 0 && collections.length === 0

  return (
    <SettingsSection
      id="trash"
      title="Deleted content"
      description="Recover collections and articles deleted from your knowledge base."
    >
      {isLoading ? (
        <Text variant="body-sm" className="text-foreground/50">
          Loading deleted content...
        </Text>
      ) : isEmpty ? (
        <Text variant="body-sm" className="text-foreground/50">
          No deleted content.
        </Text>
      ) : (
        <div className="flex flex-col gap-6">
          {collections.length > 0 && (
            <div>
              <Text variant="label" className="mb-2">
                Collections
              </Text>
              <div className="divide-y divide-foreground/10 rounded-xl border border-foreground/10">
                {collections.map((collection) => (
                  <div
                    key={collection.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <Text variant="body-sm" className="truncate font-medium">
                        {collection.title}
                      </Text>
                      <Text variant="caption" className="text-foreground/45">
                        Deleted{' '}
                        {new Date(collection.deletedAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Text>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      loading={recoverCollection.isPending}
                      onClick={() => recoverCollection.mutate(collection.id)}
                    >
                      Recover
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {articles.length > 0 && (
            <div>
              <Text variant="label" className="mb-2">
                Articles
              </Text>
              <div className="divide-y divide-foreground/10 rounded-xl border border-foreground/10">
                {articles.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <Text variant="body-sm" className="truncate font-medium">
                        {article.title}
                      </Text>
                      <Text variant="caption" className="text-foreground/45">
                        Deleted{' '}
                        {new Date(article.deletedAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Text>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      loading={recoverArticle.isPending}
                      onClick={() => recoverArticle.mutate(article.id)}
                    >
                      Recover
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </SettingsSection>
  )
}
